<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { Link } from 'svelte-routing';

  let reports = [];
  let isLoading = true;
  let error = '';
  
  // Filters & Pagination
  let filterType = ''; // '' (All), 'promotion', 'product'
  let filterUserId = '';
  let page = 1;
  const pageSize = 10;
  let totalPages = 1;

  onMount(fetchReports);

  // Re-fetch when filters change
  $: if (filterType || filterUserId === '' || filterUserId) {
      page = 1;
      fetchReports();
  }

  async function fetchReports() {
    isLoading = true;
    error = '';
    
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('pageSize', pageSize);
    if (filterType) params.append('targetType', filterType);
    if (filterUserId) params.append('userId', filterUserId);

    try {
      const res = await authFetch(getApiUrl(`/admin/reports?${params.toString()}`));
      if (!res.ok) throw new Error('Failed to load reports.');
      
      const data = await res.json();
      reports = data.data || [];
      totalPages = data.totalPages || 1;
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function goToPage(newPage) {
      if (newPage >= 1 && newPage <= totalPages) {
          page = newPage;
          fetchReports(); // Don't reset page to 1 here
      }
  }

  function getTargetLink(report) {
      if (report.targetType === 'promotion') {
          return `/promotions/detail/${report.targetId}`;
      } else {
          // Assuming we edit products to view them, or implement a product detail page later
          return `/products/edit/${report.targetId}`;
      }
  }
</script>

<div class="admin-container">
  <h2>Reported Issues</h2>

  <!-- Filters -->
  <div class="filters">
      <div class="form-group">
          <label for="type">Target Type:</label>
          <select id="type" bind:value={filterType}>
              <option value="">All</option>
              <option value="promotion">Promotions</option>
              <option value="product">Products</option>
          </select>
      </div>
      <div class="form-group">
          <label for="uid">Reporter User ID:</label>
          <input type="text" id="uid" bind:value={filterUserId} placeholder="Filter by User ID" />
      </div>
  </div>

  <!-- Table -->
  <div class="table-container">
      {#if isLoading}
        <p>Loading...</p>
      {:else if error}
        <p class="error">{error}</p>
      {:else if reports.length === 0}
        <p>No reports found.</p>
      {:else}
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Reporter</th>
                    <th>Type</th>
                    <th>Item Name</th>
                    <th>Reason</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                {#each reports as r}
                    <tr>
                        <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                        <td>
                            <div class="reporter">
                                <strong>{r.reporterName || 'Unknown'}</strong>
                                <small>{r.reporterId}</small>
                            </div>
                        </td>
                        <td><span class="badge {r.targetType}">{r.targetType}</span></td>
                        <td>{r.targetName}</td>
                        <td>{r.reason}</td>
                        <td>
                            <Link to={getTargetLink(r)} class="view-btn">View Item</Link>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
      {/if}
  </div>

  <!-- Pagination -->
  <div class="pagination">
      <button disabled={page <= 1} on:click={() => goToPage(page - 1)}>Previous</button>
      <span>Page {page} of {totalPages}</span>
      <button disabled={page >= totalPages} on:click={() => goToPage(page + 1)}>Next</button>
  </div>
</div>

<style>
  .admin-container { max-width: 1000px; margin: 2rem auto; padding: 0 1rem; }
  h2 { margin-bottom: 2rem; color: #333; }
  
  .filters {
      display: flex;
      gap: 1rem;
      background: #fff;
      padding: 1rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
  }
  .form-group { display: flex; flex-direction: column; min-width: 200px; }
  label { font-size: 0.9rem; font-weight: 500; margin-bottom: 0.3rem; }
  input, select { padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; }

  .table-container { overflow-x: auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
  table { width: 100%; border-collapse: collapse; min-width: 600px; }
  th, td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #eee; }
  thead { background-color: #f8f9fa; }
  th { font-weight: 600; color: #555; }
  
  .reporter { display: flex; flex-direction: column; }
  .reporter small { color: #888; font-size: 0.8rem; }
  
  .badge { padding: 3px 8px; border-radius: 12px; font-size: 0.85rem; font-weight: bold; text-transform: capitalize; }
  .badge.promotion { background-color: #e3f2fd; color: #0d47a1; }
  .badge.product { background-color: #fce4ec; color: #c2185b; }

  .view-btn { text-decoration: none; color: #007bff; font-weight: 500; }
  .view-btn:hover { text-decoration: underline; }

  .pagination { display: flex; justify-content: flex-end; align-items: center; gap: 1rem; margin-top: 1rem; }
  button { padding: 0.5rem 1rem; cursor: pointer; }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
  .error { color: red; }
</style>
